/**
 * Module Rapports : domaine (ADR-023, `docs/rapports/README.md`).
 *
 * Les clés de données, de rapports et de filtres sont celles des fonctions SQL (`report_dataset_rules_v1`,
 * `report_financial_rules_v1`, `report_filters_valid_v1`) et de la fonction Edge `report-exports`. Les périodes
 * se calculent à la date locale de l'entreprise; la saison va du 1er août au 31 juillet.
 */

export type ReportJobKind = 'EXPORT' | 'FINANCIAL_REPORT' | 'BACKUP'
export type ReportJobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'expired'

export type DatasetFamily = 'CLIENTS' | 'CONTRACTS' | 'BILLING' | 'OPERATIONS' | 'ORGANIZATION'

export type DatasetKey =
  | 'clients'
  | 'client_addresses'
  | 'client_contacts'
  | 'contracts_residential'
  | 'contracts_commercial'
  | 'contracts_multifamily'
  | 'contracts_vip'
  | 'invoices'
  | 'payments'
  | 'taxes'
  | 'routes'
  | 'route_addresses'
  | 'missions'
  | 'operations'
  | 'employees'
  | 'equipments'
  | 'leads'
  | 'quotes'
  | 'sms_consents'
  | 'incidents'

export type FilterKey =
  | 'contractStatus'
  | 'contractType'
  | 'paymentStatus'
  | 'routeIds'
  | 'sectorIds'
  | 'vip'
  | 'operatorIds'
  | 'equipmentIds'
  | 'missionStatus'
  | 'operationIds'

export interface DatasetDefinition {
  key: DatasetKey
  family: DatasetFamily
  label: string
  /** Filtres appliqués par la lecture SQL de cette donnée; les autres ne la changent pas. */
  filters: readonly FilterKey[]
}

export const DATASET_FAMILIES: readonly { key: DatasetFamily; label: string }[] = [
  { key: 'CLIENTS', label: 'Clients' },
  { key: 'CONTRACTS', label: 'Contrats' },
  { key: 'BILLING', label: 'Facturation' },
  { key: 'OPERATIONS', label: 'Opérations' },
  { key: 'ORGANIZATION', label: 'Organisation et suivi' },
]

const CONTRACT_FILTERS: readonly FilterKey[] = [
  'contractStatus',
  'contractType',
  'routeIds',
  'sectorIds',
  'vip',
]

export const DATASETS: readonly DatasetDefinition[] = [
  { key: 'clients', family: 'CLIENTS', label: 'Clients', filters: ['routeIds', 'sectorIds', 'vip'] },
  { key: 'client_addresses', family: 'CLIENTS', label: 'Adresses', filters: [] },
  { key: 'client_contacts', family: 'CLIENTS', label: 'Contacts', filters: [] },
  {
    key: 'contracts_residential',
    family: 'CONTRACTS',
    label: 'Contrats résidentiels',
    filters: CONTRACT_FILTERS,
  },
  {
    key: 'contracts_commercial',
    family: 'CONTRACTS',
    label: 'Contrats commerciaux',
    filters: CONTRACT_FILTERS,
  },
  {
    key: 'contracts_multifamily',
    family: 'CONTRACTS',
    label: 'Contrats multilogements',
    filters: CONTRACT_FILTERS,
  },
  { key: 'contracts_vip', family: 'CONTRACTS', label: 'Contrats VIP', filters: CONTRACT_FILTERS },
  {
    key: 'invoices',
    family: 'BILLING',
    label: 'Factures',
    filters: ['contractStatus', 'contractType', 'paymentStatus', 'routeIds', 'sectorIds', 'vip'],
  },
  {
    key: 'payments',
    family: 'BILLING',
    label: 'Encaissements',
    filters: ['contractType', 'routeIds', 'sectorIds'],
  },
  { key: 'taxes', family: 'BILLING', label: 'Taxes perçues', filters: ['contractType'] },
  {
    key: 'routes',
    family: 'OPERATIONS',
    label: 'Routes',
    filters: ['equipmentIds', 'operatorIds', 'routeIds', 'sectorIds'],
  },
  {
    key: 'route_addresses',
    family: 'OPERATIONS',
    label: 'Adresses des routes',
    filters: ['contractStatus', 'routeIds', 'sectorIds', 'vip'],
  },
  {
    key: 'missions',
    family: 'OPERATIONS',
    label: 'Missions',
    filters: ['equipmentIds', 'missionStatus', 'operationIds', 'operatorIds', 'routeIds', 'sectorIds'],
  },
  { key: 'operations', family: 'OPERATIONS', label: 'Opérations de déneigement', filters: ['operationIds'] },
  { key: 'employees', family: 'ORGANIZATION', label: 'Employés', filters: ['operatorIds'] },
  { key: 'equipments', family: 'ORGANIZATION', label: 'Équipements', filters: ['equipmentIds'] },
  { key: 'leads', family: 'ORGANIZATION', label: 'Demandes', filters: ['sectorIds'] },
  { key: 'quotes', family: 'ORGANIZATION', label: 'Soumissions / propositions', filters: [] },
  { key: 'sms_consents', family: 'ORGANIZATION', label: 'Consentements SMS', filters: [] },
  {
    key: 'incidents',
    family: 'ORGANIZATION',
    label: 'Journal des problèmes / incidents',
    filters: ['operationIds', 'operatorIds', 'routeIds'],
  },
]

const datasetByKey = new Map(DATASETS.map((dataset) => [dataset.key, dataset]))

export function datasetLabel(key: string): string {
  return datasetByKey.get(key as DatasetKey)?.label ?? key
}

/** Valeurs proposées : liste fixe, ou liste lue ailleurs dans l'application (routes, secteurs…). */
export type FilterOptionSource = 'routes' | 'sectors' | 'operators' | 'equipments' | 'operations'

export interface FilterGroup {
  key: FilterKey
  label: string
  options: readonly { value: string; label: string }[] | FilterOptionSource
}

export const FILTER_GROUPS: readonly FilterGroup[] = [
  {
    key: 'contractStatus',
    label: 'Statut du contrat',
    options: [
      { value: 'ACTIVE', label: 'Actif' },
      { value: 'SIGNED', label: 'Signé' },
      { value: 'AWAITING_PAYMENT', label: 'En attente de paiement' },
      { value: 'PENDING_CLIENT', label: 'En attente du client' },
      { value: 'VIEWED', label: 'Consulté' },
      { value: 'SUSPENDED', label: 'Suspendu' },
      { value: 'CANCELLED', label: 'Annulé' },
      { value: 'DECLINED', label: 'Refusé' },
      { value: 'EXPIRED', label: 'Expiré' },
      { value: 'COMPLETED', label: 'Terminé' },
    ],
  },
  {
    key: 'contractType',
    label: 'Type de contrat',
    options: [
      { value: 'RESIDENTIAL', label: 'Résidentiel' },
      { value: 'COMMERCIAL', label: 'Commercial' },
      { value: 'MULTIFAMILY', label: 'Multilogements' },
    ],
  },
  {
    key: 'paymentStatus',
    label: 'Statut de paiement',
    options: [
      { value: 'payee', label: 'Payée' },
      { value: 'partiellement_payee', label: 'Partiellement payée' },
      { value: 'envoyee', label: 'Impayée' },
      { value: 'en_retard', label: 'En retard' },
      { value: 'brouillon', label: 'Brouillon' },
      { value: 'annulee', label: 'Annulée' },
    ],
  },
  { key: 'routeIds', label: 'Route', options: 'routes' },
  { key: 'sectorIds', label: 'Secteur', options: 'sectors' },
  {
    key: 'vip',
    label: 'VIP',
    options: [
      { value: 'true', label: 'VIP seulement' },
      { value: 'false', label: 'Non VIP' },
    ],
  },
  { key: 'operatorIds', label: 'Opérateur', options: 'operators' },
  { key: 'equipmentIds', label: 'Équipement', options: 'equipments' },
  {
    key: 'missionStatus',
    label: 'Statut de mission',
    options: [
      { value: 'terminee', label: 'Terminée' },
      { value: 'terminee_avec_anomalies', label: 'Terminée avec anomalies' },
      { value: 'en_cours', label: 'En cours' },
      { value: 'planifiee', label: 'Planifiée' },
      { value: 'annulee', label: 'Annulée' },
    ],
  },
  { key: 'operationIds', label: 'Opération', options: 'operations' },
]

/** Groupes de filtres utiles pour les données cochées, dans l'ordre de la maquette. */
export function filterGroupsFor(datasets: readonly DatasetKey[]): FilterGroup[] {
  const used = new Set(datasets.flatMap((key) => datasetByKey.get(key)?.filters ?? []))
  return FILTER_GROUPS.filter((group) => used.has(group.key))
}

/** Filtres choisis : listes de valeurs, `vip` booléen. Un groupe vide n'est pas envoyé. */
export type ExportFilters = Partial<Record<Exclude<FilterKey, 'vip'>, string[]>> & { vip?: boolean }

export function compactFilters(
  filters: ExportFilters,
  datasets: readonly DatasetKey[],
): ExportFilters | undefined {
  const allowed = new Set(filterGroupsFor(datasets).map((group) => group.key))
  const result: ExportFilters = {}
  for (const [key, value] of Object.entries(filters) as [FilterKey, string[] | boolean | undefined][]) {
    if (!allowed.has(key) || value === undefined) continue
    if (key === 'vip') {
      if (typeof value === 'boolean') result.vip = value
    } else if (Array.isArray(value) && value.length > 0) result[key] = [...new Set(value)]
  }
  return Object.keys(result).length > 0 ? result : undefined
}

/** Option d'un filtre : ID (comme le filtre SQL l'attend), libellé, précision facultative (saison, « Inactif »). */
export interface FilterOption {
  value: string
  label: string
  detail?: string | undefined
}

/** Options tirées de la base (`report_filter_options_v1`), par source. */
export type FilterOptions = Record<FilterOptionSource, FilterOption[]>

/** Valeurs cochées par groupe, telles que l'écran les garde. */
export type FilterSelection = Partial<Record<FilterKey, string[]>>

/**
 * Sélection de l'écran en filtres de commande. `vip` est un choix unique (`true` / `false`); le reste, des listes.
 * Les groupes qui ne concernent aucune donnée cochée sont retirés (`compactFilters`).
 */
export function selectionToFilters(
  selection: FilterSelection,
  datasets: readonly DatasetKey[],
): ExportFilters | undefined {
  const filters: ExportFilters = {}
  for (const [key, values] of Object.entries(selection) as [FilterKey, string[] | undefined][]) {
    if (!values || values.length === 0) continue
    if (key === 'vip') {
      if (values.length === 1) filters.vip = values[0] === 'true'
    } else filters[key] = values
  }
  return compactFilters(filters, datasets)
}

/** Résumé d'un groupe : « Tous », le libellé de la seule valeur, ou « 3 sélectionnés ». */
export function filterGroupSummary(values: readonly string[], options: readonly FilterOption[]): string {
  if (values.length === 0) return 'Tous'
  if (values.length === 1)
    return options.find((option) => option.value === values[0])?.label ?? '1 sélectionné'
  return `${String(values.length)} sélectionnés`
}

// Périodes -----------------------------------------------------------------------------------------------------

export type PeriodKey = 'SEASON' | 'MONTH' | 'YEAR' | 'PREVIOUS_SEASON' | 'ALL' | 'CUSTOM'

export const PERIOD_OPTIONS: readonly { key: PeriodKey; label: string }[] = [
  { key: 'SEASON', label: 'Cette saison' },
  { key: 'MONTH', label: 'Mois courant' },
  { key: 'YEAR', label: 'Année courante' },
  { key: 'PREVIOUS_SEASON', label: 'Saison précédente' },
  { key: 'ALL', label: 'Toutes les données' },
  { key: 'CUSTOM', label: 'Période personnalisée' },
]

/** Rapports financiers : mêmes périodes, sans « Toutes les données ». */
export const FINANCIAL_PERIOD_OPTIONS = PERIOD_OPTIONS.filter((option) => option.key !== 'ALL')

export interface Period {
  key: PeriodKey
  /** `AAAA-MM-JJ`, inclusifs; `null` : sans borne. */
  start: string | null
  end: string | null
}

export const COMPANY_TIME_ZONE = 'America/Toronto'

/** Date locale `AAAA-MM-JJ` de l'entreprise. */
export function companyToday(instant: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: COMPANY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant)
}

const pad = (value: number) => String(value).padStart(2, '0')

/** `AAAA-MM-JJ` en [année, mois, jour]; 0 pour une partie absente (la validation est faite ailleurs). */
function dateParts(date: string): [number, number, number] {
  const [year = 0, month = 0, day = 0] = date.split('-').map(Number)
  return [year, month, day]
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/** Année de début de la saison qui contient `today` (`2026-10-30` : 2026; `2027-03-01` : 2026). */
export function seasonStartYear(today: string): number {
  const [year, month] = dateParts(today)
  return month >= 8 ? year : year - 1
}

export function seasonLabel(startYear: number): string {
  return `${String(startYear)}-${String(startYear + 1)}`
}

export class ReportPeriodError extends Error {
  constructor() {
    super('La date de fin doit être égale ou postérieure à la date de début.')
    this.name = 'ReportPeriodError'
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Bornes d'une période à la date `today`. `CUSTOM` : dates saisies, fin ≥ début (sinon `ReportPeriodError`). */
export function resolvePeriod(
  key: PeriodKey,
  today: string,
  custom: { start: string; end: string } = { start: '', end: '' },
): Period {
  const [year, month] = dateParts(today)
  switch (key) {
    case 'SEASON': {
      const start = seasonStartYear(today)
      return { key, start: `${String(start)}-08-01`, end: `${String(start + 1)}-07-31` }
    }
    case 'PREVIOUS_SEASON': {
      const start = seasonStartYear(today) - 1
      return { key, start: `${String(start)}-08-01`, end: `${String(start + 1)}-07-31` }
    }
    case 'MONTH':
      return {
        key,
        start: `${String(year)}-${pad(month)}-01`,
        end: `${String(year)}-${pad(month)}-${pad(lastDayOfMonth(year, month))}`,
      }
    case 'YEAR':
      return { key, start: `${String(year)}-01-01`, end: `${String(year)}-12-31` }
    case 'ALL':
      return { key, start: null, end: null }
    case 'CUSTOM': {
      if (!ISO_DATE.test(custom.start) || !ISO_DATE.test(custom.end) || custom.end < custom.start)
        throw new ReportPeriodError()
      return { key, start: custom.start, end: custom.end }
    }
  }
}

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

const SHORT_MONTHS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juill.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
]

/** `2026-10-30` : « 30 oct. 2026 »; `2026-08-01` : « 1er août 2026 » (abréviations de l'OQLF). */
export function shortDate(date: string): string {
  const [year, month, day] = dateParts(date)
  return `${day === 1 ? '1er' : String(day)} ${SHORT_MONTHS[month - 1] ?? ''} ${String(year)}`
}

/** Libellé lisible d'une période (résumé d'un export, Historique). */
export function periodLabel(period: {
  key?: string | null
  start: string | null
  end: string | null
}): string {
  const { start, end } = period
  if (!start && !end) return 'Toutes les données'
  if (start && end) {
    const [startYear, startMonth, startDay] = dateParts(start)
    const [endYear, endMonth, endDay] = dateParts(end)
    if (startMonth === 8 && startDay === 1 && endMonth === 7 && endDay === 31 && endYear === startYear + 1)
      return `Saison ${seasonLabel(startYear)}`
    if (startMonth === 1 && startDay === 1 && endMonth === 12 && endDay === 31 && endYear === startYear)
      return `Année ${String(startYear)}`
    if (
      startYear === endYear &&
      startMonth === endMonth &&
      startDay === 1 &&
      endDay === lastDayOfMonth(endYear, endMonth)
    ) {
      const name = MONTHS[startMonth - 1] ?? ''
      return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${String(startYear)}`
    }
    return `${shortDate(start)} – ${shortDate(end)}`
  }
  return start ? `Depuis le ${shortDate(start)}` : `Jusqu’au ${shortDate(end ?? '')}`
}

/** Précision d'une tuile de période : les bornes, ou ce que la période couvre. */
export function periodDetail(period: Period): string {
  if (period.key === 'CUSTOM') return 'Choisir les dates'
  if (period.key === 'ALL') return 'Sans limite de date'
  if (period.key === 'PREVIOUS_SEASON' && period.start) return seasonLabel(dateParts(period.start)[0])
  if (period.key === 'MONTH') return periodLabel(period)
  return period.start && period.end ? `${shortDate(period.start)} – ${shortDate(period.end)}` : ''
}

// Rapports financiers ------------------------------------------------------------------------------------------

export type FinancialReportKey = 'SALES_SUMMARY' | 'TAXES' | 'PAYMENTS' | 'RECEIVABLES' | 'REVENUE'
export type RevenueAxis = 'CATEGORY' | 'SEASON' | 'SECTOR' | 'ROUTE'

export interface FinancialReportDefinition {
  key: FinancialReportKey
  title: string
  description: string
  /** Permission de lecture de la source, en plus de `report.export_financial`. */
  readPermission: 'invoice.read' | 'payment.read'
}

export const FINANCIAL_REPORTS: readonly FinancialReportDefinition[] = [
  {
    key: 'SALES_SUMMARY',
    title: 'Sommaire des ventes',
    description: 'Sous-total avant taxes, TPS, TVQ et total facturé.',
    readPermission: 'invoice.read',
  },
  {
    key: 'TAXES',
    title: 'Taxes perçues',
    description: 'Montant taxable, TPS et TVQ perçues sur la période.',
    readPermission: 'invoice.read',
  },
  {
    key: 'PAYMENTS',
    title: 'Encaissements',
    description: 'Paiements reçus par date, méthode et facture associée.',
    readPermission: 'payment.read',
  },
  {
    key: 'RECEIVABLES',
    title: 'Comptes clients',
    description: 'Montant facturé, payé, solde restant et retards.',
    readPermission: 'invoice.read',
  },
  {
    key: 'REVENUE',
    title: 'Revenus',
    description: 'Revenus avant taxes par catégorie, saison, secteur ou route.',
    readPermission: 'invoice.read',
  },
]

export const REVENUE_AXES: readonly { key: RevenueAxis; label: string }[] = [
  { key: 'CATEGORY', label: 'Catégorie' },
  { key: 'SEASON', label: 'Saison' },
  { key: 'SECTOR', label: 'Secteur' },
  { key: 'ROUTE', label: 'Route' },
]

export function financialReportTitle(key: string, axis?: string | null): string {
  const title = FINANCIAL_REPORTS.find((report) => report.key === key)?.title ?? key
  const axisLabel = REVENUE_AXES.find((option) => option.key === axis)?.label
  return axisLabel ? `${title} par ${axisLabel.toLowerCase()}` : title
}

// Historique ---------------------------------------------------------------------------------------------------

export interface ReportJob {
  jobId: string
  kind: ReportJobKind
  category: string
  parameters: {
    datasets?: string[] | undefined
    filters?: Record<string, unknown> | undefined
    periodKey?: string | undefined
    axis?: string | undefined
  }
  periodStart: string | null
  periodEnd: string | null
  reason: string | null
  status: ReportJobStatus
  progressPercent: number
  currentStep: string | null
  requestedBy: { userId: string; name: string | null }
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  expiresAt: string | null
  snapshotAt: string | null
  fileName: string | null
  sizeBytes: number | null
  rowCounts: Record<string, number> | null
  errorCode: string | null
  errorMessage: string | null
  deletedAt: string | null
}

export const JOB_KIND_LABELS: Record<ReportJobKind, string> = {
  EXPORT: 'Export',
  FINANCIAL_REPORT: 'Rapport',
  BACKUP: 'Sauvegarde complète',
}

export const JOB_STATUS_LABELS: Record<ReportJobStatus, string> = {
  queued: 'En attente',
  processing: 'En préparation',
  completed: 'Terminé',
  failed: 'Échec',
  expired: 'Expiré',
}

/** Ton du badge de statut (maquette : préparation info, terminé succès, échec danger, expiré neutre). */
export const JOB_STATUS_TONES: Record<ReportJobStatus, 'info' | 'success' | 'danger' | 'neutral'> = {
  queued: 'info',
  processing: 'info',
  completed: 'success',
  failed: 'danger',
  expired: 'neutral',
}

function companyParts(instant: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: COMPANY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instant))
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? ''
  return {
    date: `${part('year')}-${part('month')}-${part('day')}`,
    time: `${part('hour')}:${part('minute')}`,
  }
}

/** Instant en heure de l'entreprise : `{ date: '30 oct. 2026', time: '09:12' }`. */
export function companyDateTime(instant: string): { date: string; time: string } {
  const { date, time } = companyParts(instant)
  return { date: shortDate(date), time }
}

export function isJobPending(job: Pick<ReportJob, 'status'>): boolean {
  return job.status === 'queued' || job.status === 'processing'
}

/** Contenu d'une entrée : « Missions · Octobre 2026 », « Revenus par route · Saison 2026-2027 », « Toute la base ». */
export function describeJob(job: ReportJob): string {
  if (job.kind === 'BACKUP') return 'Toute la base'
  const period = periodLabel({ start: job.periodStart, end: job.periodEnd })
  if (job.kind === 'FINANCIAL_REPORT')
    return `${financialReportTitle(job.category, job.parameters.axis)} · ${period}`
  const labels = (job.parameters.datasets ?? []).map(datasetLabel)
  const content =
    labels.length > 2
      ? `${labels.slice(0, 2).join(', ')} et ${String(labels.length - 2)} autres`
      : labels.join(', ')
  return `${content} · ${period}`
}

/** Taille lisible : « 64 Ko », « 48,2 Mo ». */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—'
  if (bytes < 1024) return `${String(bytes)} o`
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} Ko`
  return `${(bytes / (1024 * 1024)).toLocaleString('fr-CA', { maximumFractionDigits: 1 })} Mo`
}

/** Nom du fichier d'un export, comme la fonction Edge le produira (résumé de l'écran Exports). */
export function exportFileName(datasets: readonly DatasetKey[], today: string): string {
  const families = new Set(datasets.map((key) => datasetByKey.get(key)?.family))
  const labels: Record<DatasetFamily, string> = {
    CLIENTS: 'CLIENTS',
    CONTRACTS: 'CONTRATS',
    BILLING: 'FACTURATION',
    OPERATIONS: 'OPERATIONS',
    ORGANIZATION: 'ORGANISATION',
  }
  const [family] = [...families]
  return `RECA_EXPORT${families.size === 1 && family ? `_${labels[family]}` : ''}_${today}.xlsx`
}

// Aperçus ------------------------------------------------------------------------------------------------------

export interface DatasetPreview {
  dataset: DatasetKey
  family: DatasetFamily
  allowed: boolean
  count: number | null
  /** Permission qui manque pour une donnée verrouillée. */
  missingPermission: string | null
}

export interface FinancialSummaryLine {
  key: string
  label: string
  amount: number
  detail?: string | undefined
}

export interface FinancialPreview {
  report: FinancialReportKey
  summary: FinancialSummaryLine[]
  sheet: string | null
  rowCount: number
  rows: Record<string, unknown>[]
}

/** Ligne de total de chaque rapport, affichée à part sous les autres; Comptes clients n'en a pas. */
const TOTAL_LINE: Partial<Record<FinancialReportKey, string>> = {
  SALES_SUMMARY: 'total',
  TAXES: 'taxes',
  PAYMENTS: 'total',
  REVENUE: 'revenue',
}

/** Lignes à mettre en évidence (Comptes clients : montant en retard). */
const ALERT_LINE: Partial<Record<FinancialReportKey, string>> = { RECEIVABLES: 'overdue' }

export interface FinancialPreviewView {
  lines: (FinancialSummaryLine & { alert: boolean })[]
  total: FinancialSummaryLine | null
  /** Aucun document dans la période : rien à générer. */
  empty: boolean
}

export function financialPreviewView(preview: FinancialPreview): FinancialPreviewView {
  const totalKey = TOTAL_LINE[preview.report]
  const total = preview.summary.find((line) => line.key === totalKey) ?? null
  return {
    lines: preview.summary
      .filter((line) => line !== total)
      .map((line) => ({ ...line, alert: line.key === ALERT_LINE[preview.report] && line.amount > 0 })),
    total,
    empty: preview.rowCount === 0 && preview.summary.every((line) => line.amount === 0),
  }
}

/** Montant en dollars : « 1 234,50 $ ». */
export function formatAmount(amount: number): string {
  return amount.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })
}

/** Note d'une entrée : disponibilité, progression, cause de l'échec ou suppression du fichier. */
export function jobNote(job: ReportJob): string {
  if (isJobPending(job)) return `Préparation · ${String(job.progressPercent)} %`
  if (job.status === 'failed')
    return job.errorMessage ?? 'La génération n’a pas pu être terminée. Aucune donnée n’a été modifiée.'
  if (job.status === 'expired') {
    if (job.deletedAt) return `Fichier supprimé le ${companyDateTime(job.deletedAt).date}`
    return job.expiresAt
      ? `Fichier supprimé automatiquement le ${companyDateTime(job.expiresAt).date}`
      : 'Fichier expiré'
  }
  return job.expiresAt ? `Disponible jusqu’au ${companyDateTime(job.expiresAt).date}` : 'Disponible'
}

// Sauvegardes --------------------------------------------------------------------------------------------------

/** Conservation des fichiers (décision du 30 octobre 2026; la maquette disait 14 jours). */
export const RETENTION_DAYS = 30

export interface ArchiveEntry {
  name: string
  description: string
  /** Fichiers au plus : un fichier vide n'est pas écrit. `null` pour le manifeste. */
  maxFiles: number | null
}

/**
 * Contenu de l'archive, dans l'ordre du catalogue `report_backup_catalog_v1` (une table par fichier), plus les
 * fichiers calculés par la fonction Edge : `Facturation/taxes.xlsx` et `Systeme/valeurs_longues.xlsx`.
 */
export const BACKUP_ARCHIVE: readonly ArchiveEntry[] = [
  {
    name: '00_INFORMATIONS_SAUVEGARDE.xlsx',
    description: 'Manifeste : date, version du format, lignes et colonnes de chaque fichier',
    maxFiles: null,
  },
  { name: 'Clients/', description: 'Clients, adresses, contacts, notes, historique', maxFiles: 5 },
  {
    name: 'Contrats/',
    description: 'Contrats, adresses de service, échéanciers, avenants, signatures, zones',
    maxFiles: 16,
  },
  {
    name: 'Facturation/',
    description: 'Factures, encaissements, notes de crédit, versements Stripe, taxes',
    maxFiles: 12,
  },
  {
    name: 'Operations/',
    description: 'Opérations, missions, pauses, incidents, positions GPS',
    maxFiles: 19,
  },
  {
    name: 'Routes/',
    description: 'Routes, résidences par route, parcours, plans de répartition',
    maxFiles: 15,
  },
  {
    name: 'Organisation/',
    description: 'Utilisateurs, rôles, employés, équipements, heures moteur, secteurs',
    maxFiles: 19,
  },
  { name: 'Commercial/', description: 'Demandes, soumissions, campagnes de renouvellement', maxFiles: 18 },
  {
    name: 'Communications/',
    description: 'Consentements SMS, conversations et messages SMS, courriels',
    maxFiles: 19,
  },
  {
    name: 'Systeme/',
    description: 'Paramètres, saisons, taxes, calendriers, gabarits, documents',
    maxFiles: 20,
  },
]

/** `RECA_SAUVEGARDE_AAAA-MM-JJ_HH-mm`, à l'heure de l'entreprise (dossier racine de l'archive). */
export function backupBaseName(instant: string): string {
  const { date, time } = companyParts(instant)
  return `RECA_SAUVEGARDE_${date}_${time.replace(':', '-')}`
}

export interface BackupEstimate {
  rows: number | null
  sizeBytes: number | null
  minutes: number | null
}

/** Estimation tirée de la dernière sauvegarde terminée (lignes, taille, durée); rien avant la première. */
export function backupEstimate(jobs: readonly ReportJob[]): BackupEstimate {
  const last = jobs
    .filter((job) => job.kind === 'BACKUP' && job.completedAt !== null && job.sizeBytes !== null)
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))[0]
  if (!last) return { rows: null, sizeBytes: null, minutes: null }
  const rows = last.rowCounts ? Object.values(last.rowCounts).reduce((sum, count) => sum + count, 0) : null
  const minutes =
    last.startedAt && last.completedAt
      ? Math.max(1, Math.round((Date.parse(last.completedAt) - Date.parse(last.startedAt)) / 60_000))
      : null
  return { rows, sizeBytes: last.sizeBytes, minutes }
}

// Commandes ----------------------------------------------------------------------------------------------------

export type BeginReportCommand =
  | {
      kind: 'EXPORT'
      datasets: DatasetKey[]
      filters?: ExportFilters
      period: Period
    }
  | { kind: 'FINANCIAL_REPORT'; report: FinancialReportKey; axis?: RevenueAxis; period: Period }
  | { kind: 'BACKUP'; reason: string }

const errorMessages: Record<string, string> = {
  REPORT_FORBIDDEN: 'Vous n’avez pas accès à cette action.',
  REPORT_DATASET_FORBIDDEN: 'Une des données choisies ne vous est pas permise.',
  REPORT_DATASET_UNKNOWN: 'Une des données choisies n’existe pas.',
  REPORT_UNKNOWN: 'Ce rapport n’existe pas.',
  REPORT_COMMAND_INVALID: 'La demande est incomplète. Vérifiez vos choix et réessayez.',
  REPORT_PERIOD_INVALID: 'La date de fin doit être égale ou postérieure à la date de début.',
  REPORT_FILTERS_INVALID: 'Un des filtres n’est pas valide. Réinitialisez les filtres et réessayez.',
  REPORT_REASON_REQUIRED: 'Indiquez le motif de la sauvegarde (3 à 500 caractères).',
  REPORT_TOO_MANY_PENDING: 'Trois générations sont déjà en préparation. Attendez qu’une se termine.',
  REPORT_BACKUP_IN_PROGRESS: 'Une sauvegarde est déjà en préparation.',
  REPORT_JOB_NOT_FOUND: 'Cette entrée de l’historique est introuvable.',
  REPORT_FILE_EXPIRED: 'Ce fichier a expiré ou a été supprimé. Recréez-le au besoin.',
  REPORT_FILE_NOT_READY: 'Le fichier n’est pas encore prêt.',
  REPORT_DELETE_FORBIDDEN: 'Vous n’avez pas accès à la suppression des fichiers.',
  REPORT_JOB_NOT_DELETABLE: 'Seul un fichier terminé peut être supprimé.',
  REPORT_DOWNLOAD_FAILED: 'Le téléchargement n’a pas pu être préparé. Réessayez dans un instant.',
}

export class ReportCommandError extends Error {
  constructor(public readonly code: string) {
    super(errorMessages[code] ?? 'L’opération n’a pas pu être terminée. Réessayez dans un instant.')
    this.name = 'ReportCommandError'
  }
}

/** Code métier (`raise exception 'CODE'`) extrait d'une erreur Supabase, sinon `UNEXPECTED`. */
export function reportErrorCode(message: string | undefined): string {
  const code = message?.trim() ?? ''
  return /^[A-Z][A-Z_]+$/.test(code) ? code : 'UNEXPECTED'
}

/** Motif d'une sauvegarde : 3 à 500 caractères utiles. */
export function validBackupReason(reason: string): boolean {
  const length = reason.trim().length
  return length >= 3 && length <= 500
}
